/**
 * useSidebarNavigation Hook
 *
 * Maneja la lógica de navegación y contexto del sidebar
 */

import { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProject } from '@/shared/contexts/ProjectContext';
import { Context } from '../types/sidebar.types';
import { mockProjects } from '@/data/mock/projects';
import { normalizePath } from '../utils/sidebar.utils';

// Create contexts array: Global view + individual projects
const MOCK_PROJECTS: Context[] = [
  {
    type: 'global',
    id: 'global',
    label: '🌍 Vista Global',
  },
  ...mockProjects.map(project => ({
    type: 'project' as const,
    id: project.id,
    label: `📁 ${project.name}`,
    projectName: project.name,
  }))
];

export const useSidebarNavigation = (
  initialContext?: Context,
  onContextChange?: (context: Context) => void
) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { viewType, selectedProject, setGlobalView, setProjectView } = useProject();
  // Derivar contexto directamente del ProjectContext (sin estado duplicado)
  const currentContext = useMemo((): Context => {
    if (viewType === 'project' && selectedProject) {
      const projectContext = MOCK_PROJECTS.find(p => p.id === selectedProject.id);
      if (projectContext) return projectContext;
    }

    // Fallback a global
    return MOCK_PROJECTS.find(p => p.type === 'global') || MOCK_PROJECTS[0];
  }, [viewType, selectedProject]);

  // Manejar navegación con rutas limpias
  const handleNavigate = (href: string) => {
    const path = normalizePath(href.startsWith('/') ? href : `/${href}`);
    try {
      navigate(path);
    } catch (error) {
      console.error('🔴 [NAV] Navigation error:', error);
      window.location.href = path;
    }
  };

  // Manejar cambio de contexto
  const handleContextChange = (contextId: string) => {
    const selected = MOCK_PROJECTS.find(p => (p.id || 'global') === contextId);

    if (selected) {
      // Actualizar ProjectContext global (esto automáticamente actualiza currentContext via useMemo)
      if (selected.type === 'global') {
        setGlobalView();
      } else if (selected.id) {
        setProjectView(selected.id);
      }

      // Callback opcional
      if (onContextChange) {
        onContextChange(selected);
      }
    }
  };

  // Current path for active state comparison (clean URLs, no role prefix)
  const normalizedPath = location.pathname;

  return {
    currentContext,
    availableContexts: MOCK_PROJECTS,
    currentPath: normalizedPath,
    handleNavigate,
    handleContextChange,
  };
};
